import { Plus } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import DataTable from '@/components/DataTable';
import ColumnCustomizer from '@/components/DataTable/ColumnCustomizer';
import Sorter from '@/components/DataTable/Sorter';
import DataFilter from '@/components/Filter/DataFilter';
import SearchInput from '@/components/Inputs/SearchInput';
import { PageHeaderTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { useAgentHeader, useAgentTableData } from '@/data-table-configs/agents';
import CreateAgentModal from '@/features/Agent/components/CreateAgentModal';
import useAgentStore from '@/features/Agent/stores';
import PageHeaderStyle from '@/features/Commons/PageLayout/PageHeader';
import useColor from '@/hooks/domain/useColor';
import { useStorageFilterQuery } from '@/hooks/domain/useFilterQuery';
import useRedirect from '@/hooks/domain/useRedirect';
import useUserRole from '@/hooks/domain/useUserRole';
import useFetch from '@/hooks/pure/useFetch';
import useStates from '@/hooks/pure/useStates';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import { filterMap } from '@/utils/lazy';
import { highlight, removeHighlight } from '@/utils/util';

const AgentPage = () => {
  const color = useColor();
  const { isEditor } = useUserRole();

  useRedirect();

  const { currFilterSetQuery, onFilterChange, clearFilter } =
    useStorageFilterQuery(STORAGE_KEYS.AGENT_FILTER_STATUS_KEY);

  const [sortStatus, setSortStatus] = useState<NotNullableSortStatusT>({
    sortBy: 'name',
    orderStatus: 'asc',
  });

  const {
    createAgentModalStatus,
    selectedAgent,
    setAgentState,
    fetchAgentList,
  } = useAgentStore((state) => ({
    createAgentModalStatus: state.createAgentModalStatus,
    selectedAgent: state.selectedAgent,
    setAgentState: state.setState,
    fetchAgentList: state.fetchAgentList,
  }));

  const { toggleColumnVisible, changeColumnOrder, changeColumnWidth } =
    useColumnConfigStore(
      ({ toggleColumnVisible, changeColumnOrder, changeColumnWidth }) => ({
        toggleColumnVisible,
        changeColumnOrder,
        changeColumnWidth,
      }),
    );

  type States = {
    searchValue: string;
    searchInput: string;
  };

  const { state, setValue, setState } = useStates<States>({
    searchValue: '',
    searchInput: '',
  });

  const clearSearch = () => {
    setState({
      searchInput: '',
      searchValue: '',
    });
  };

  const agentHeader = useAgentHeader();
  const tableData = useAgentTableData({
    searchValue: state.searchValue,
    filterSetQuery: currFilterSetQuery,
    sortStatus,
  });

  useFetch(fetchAgentList, !isEditor);

  return (
    <Style>
      <PageHeaderStyle>
        <Flex g={12} a="center" height="100%">
          <PageHeaderTitle>Agents</PageHeaderTitle>
          <Typo type="label1" color={color.gray600}>
            {tableData.length}
          </Typo>
          <SearchInput
            placeholder="Search"
            value={state.searchInput}
            onChange={(e) => setValue('searchInput', e.currentTarget.value)}
            onBlur={() => {
              setValue('searchValue', state.searchInput);
              if (state.searchInput) {
                highlight(state.searchInput);
              } else {
                removeHighlight();
              }
            }}
            onClickClear={clearSearch}
          />
        </Flex>
        {isEditor && (
          <Flex a="center" g="6px">
            <Button
              leftIcon={<Plus />}
              sizeType="m"
              onClick={() => setAgentState({ createAgentModalStatus: true })}
            >
              Agent
            </Button>
          </Flex>
        )}
      </PageHeaderStyle>
      <Flex padding="0 32px" j="space-between">
        <DataFilter
          filterOptions={agentHeader}
          tableName="agent"
          currFilterSetQuery={currFilterSetQuery}
          onFilterChange={onFilterChange}
          resetFilter={clearFilter}
        />
        <Flex>
          <Sorter
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
            options={filterMap(agentHeader, (col) => {
              if (!col.sortKey || !col.orderable) return null;
              return {
                sortKey: col.sortKey,
                sortName: col.columnName,
                orderType: col.orderType || 'default',
              };
            })}
          />
          <ColumnCustomizer
            columns={agentHeader}
            onCustomizeColumnSelect={(key) => {
              toggleColumnVisible('agent_view', key);
            }}
          />
        </Flex>
      </Flex>
      <ScrollView style={{ height: 'calc(100% - 56px)' }}>
        <DataTable
          tableHeight="calc(100vh - 64px)"
          name="agent"
          id="agent-data-table"
          columns={agentHeader}
          datas={tableData}
          selectedRowKey={selectedAgent}
          customizable
          sortStatus={sortStatus}
          onSortChange={setSortStatus}
          columnChangeOrderHandler={(key, order) =>
            changeColumnOrder('agent_view', key, order)
          }
          columnResizeHandler={(key, width) =>
            changeColumnWidth('agent_view', key, width)
          }
          onCustomizeColumnSelect={(key) =>
            toggleColumnVisible('agent_view', key)
          }
          openButtonOffsetX={120}
        />
      </ScrollView>
      {createAgentModalStatus && <CreateAgentModal />}
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 100%;
  height: 100vh;
  position: relative;
  .customer-list-header {
    width: 100%;
    padding: 16px 24px 0px 24px;
    z-index: 10;
    background-color: ${(props) => props.theme.colors.gray100};
  }
  .customer-list-filter-sort {
    width: 100%;
    padding: 0 8px;
    z-index: 10;
    background-color: ${(props) => props.theme.colors.gray100};
  }
`;

export default AgentPage;
